import { REGIONS_COUNTRY_CODES } from '@luxuryescapes/lib-regions/lib/phoneNumberValidation';

export type EnrolmentPath = 'existing' | 'new';

export type InfiniteEnrolmentLaunchApplicant = {
  firstName: string;
  lastName: string;
  email: string;
  phonePrefix: string;
  phone: string;
};

export type InfiniteEnrolmentLaunchIntent = {
  id: number;
  path: EnrolmentPath;
  applicant?: InfiniteEnrolmentLaunchApplicant;
};

export type CreateInfiniteOutcome =
  | { kind: 'success'; customerName: string; customerId: string; message: string }
  | { kind: 'email-warning'; customerName: string; customerId: string; message: string }
  | { kind: 'partial'; customerEmail: string; customerId: string; message: string }
  | { kind: 'error'; message: string };

export const DEFAULT_COUNTRY_CODE = 'AU';
export const DEFAULT_PHONE_PREFIX = '61';

/**
 * Mirrors the phone-prefix dropdown used by the user-detail Update User form.
 */
export const PHONE_PREFIX_OPTIONS: ReadonlyArray<{ value: string; label: string }> = (() => {
  const preferred = ['AU', 'NZ', 'GB', 'SG'];
  const merged = ['US', 'CA'];
  const sortedRegionCodes = [
    ...preferred,
    ...Object.keys(REGIONS_COUNTRY_CODES)
      .filter((code) => !preferred.includes(code) && !merged.includes(code))
      .sort((a, b) => REGIONS_COUNTRY_CODES[a].countryName.localeCompare(REGIONS_COUNTRY_CODES[b].countryName)),
  ];

  const items = sortedRegionCodes.map((regionCode) => {
    const region = REGIONS_COUNTRY_CODES[regionCode];
    return {
      value: region.countryCode,
      label: `${region.countryName} - ${regionCode} (+${region.countryCode})`,
    };
  });

  items.splice(2, 0, { value: '1', label: 'United States / Canada - US/CA (+1)' });
  return items;
})();

export function normalisePhonePrefix(value: string | null | undefined): string {
  const numericValue = (value ?? '').replace(/[^0-9]/g, '');
  return PHONE_PREFIX_OPTIONS.some((option) => option.value === numericValue) ? numericValue : DEFAULT_PHONE_PREFIX;
}

/** Loose RFC-style check; final validation happens server-side. */
export function isLikelyEmail(value: string): boolean {
  return /.+@.+\..+/.test(value);
}

/**
 * Generate a strong random password client-side. The password is never shown
 * to the admin; the customer will reset it via the standard forgot-password flow.
 */
export function generateRandomPassword(length = 24): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!$%&*?';
  const alphabetLength = alphabet.length;
  const maxValid = Math.floor(256 / alphabetLength) * alphabetLength;
  let out = '';
  while (out.length < length) {
    const bytes = new Uint8Array(length);
    window.crypto.getRandomValues(bytes);
    for (let i = 0; i < bytes.length && out.length < length; i += 1) {
      if (bytes[i] >= maxValid) {
        continue;
      }
      out += alphabet[bytes[i] % alphabetLength];
    }
  }
  return out;
}

export const CONCIERGE_REP_HELPER_TEXT =
  'This representative will be assigned to the account as their dedicated Infinite concierge representative.';

export const CONCIERGE_REP_FIELD_LABEL = 'Concierge representative';

export const CONCIERGE_REP_REQUIRED_ERROR = 'Select a concierge representative.';

export const EXISTING_CUSTOMER_SEARCH_LABEL = 'Name / email / phone / Société account / user ID';
